import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CLIENT_COMMS_EVENTS,
  CLIENT_COMMS_PROPERTIES,
} from '@/constants/analytics/client-communications-events';
import { useClientCommunicationsLocalState } from '@/hooks/useClientCommunicationsLocalState';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import {
  useGetClientAttachmentsQuery,
  useGetClientCommunicationsQuery,
  useSyncEmailsMutation,
} from '@/services/api/clientCommunicationsService';
import { useTreatmentSendSmsMutation } from '@/services/api/generated/treatmentSmsService';
import {
  ClientCommunicationsResponse,
  ClientCommunicationsTabId,
  CommunicationAttachment,
  UnifiedCommunication,
} from '@/services/types/client-communications-types';

// ============================================
// MOCK DATA - Set to true to enable mock data for development
// ============================================
const USE_MOCK_DATA = false;
// Set to true to add mock phone calls to real data (for testing call UI without real calls)
const USE_MOCK_CALLS = false;

const MOCK_COMMUNICATIONS: UnifiedCommunication[] = [
  // SMS Messages
  {
    id: 'sms-1',
    type: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(), // 5 mins ago
    content: 'Hi, I wanted to follow up on my case. Any updates?',
    source_id: 'conv-1',
    source_type: 'conversation',
    from_identifier: '+1 (555) 123-4567',
    to_identifiers: ['+1 (555) 987-6543'],
    attachments: [],
    has_attachments: false,
    transmission_status: 'delivered',
  },
  {
    id: 'sms-2',
    type: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 4).toISOString(), // 4 mins ago
    content:
      'Hello! Yes, we received the medical records from your provider yesterday. Our team is currently reviewing them.',
    source_id: 'conv-1',
    source_type: 'conversation',
    from_identifier: '+1 (555) 987-6543',
    to_identifiers: ['+1 (555) 123-4567'],
    attachments: [],
    has_attachments: false,
    transmission_status: 'delivered',
  },
  {
    id: 'sms-3',
    type: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 3).toISOString(), // 3 mins ago
    content: 'Great, thank you! How long does the review usually take?',
    source_id: 'conv-1',
    source_type: 'conversation',
    from_identifier: '+1 (555) 123-4567',
    to_identifiers: ['+1 (555) 987-6543'],
    attachments: [],
    has_attachments: false,
    transmission_status: 'delivered',
  },
  {
    id: 'sms-4',
    type: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 2).toISOString(), // 2 mins ago
    content:
      "Typically 3-5 business days. We'll reach out as soon as we have an update. Is this number the best way to reach you?",
    source_id: 'conv-1',
    source_type: 'conversation',
    from_identifier: '+1 (555) 987-6543',
    to_identifiers: ['+1 (555) 123-4567'],
    attachments: [],
    has_attachments: false,
    transmission_status: 'delivered',
  },
  {
    id: 'sms-5',
    type: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 1).toISOString(), // 1 min ago
    content: 'Yes, text is perfect. Thanks!',
    source_id: 'conv-1',
    source_type: 'conversation',
    from_identifier: '+1 (555) 123-4567',
    to_identifiers: ['+1 (555) 987-6543'],
    attachments: [],
    has_attachments: false,
    transmission_status: 'delivered',
  },
  // Older SMS conversation (2 days ago)
  {
    id: 'sms-old-1',
    type: 'sms',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(), // 2 days ago
    content:
      'Hi John, this is Sarah from Smith & Associates. Just confirming your appointment for tomorrow at 2pm.',
    source_id: 'conv-2',
    source_type: 'conversation',
    from_identifier: '+1 (555) 987-6543',
    to_identifiers: ['+1 (555) 123-4567'],
    attachments: [],
    has_attachments: false,
    transmission_status: 'delivered',
  },
  {
    id: 'sms-old-2',
    type: 'sms',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 47).toISOString(),
    content: "Confirmed! I'll be there. Should I bring anything?",
    source_id: 'conv-2',
    source_type: 'conversation',
    from_identifier: '+1 (555) 123-4567',
    to_identifiers: ['+1 (555) 987-6543'],
    attachments: [],
    has_attachments: false,
    transmission_status: 'delivered',
  },

  // Phone Calls
  {
    id: 'call-1',
    type: 'phone_call',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    content:
      'Discussed case status and next steps. Client confirmed receipt of settlement offer.',
    source_id: 'audio-1',
    source_type: 'audio_conversation',
    from_identifier: '+1 (555) 987-6543',
    to_identifiers: ['+1 (555) 123-4567'],
    attachments: [],
    has_attachments: false,
    duration_seconds: 847, // ~14 minutes
    transcript_available: true,
    transcript_id: 'transcript-1',
    audio_conversation_id: 'audio-1',
  },
  {
    id: 'call-2',
    type: 'phone_call',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(), // 3 days ago
    content:
      'Client called with questions about medical records request. Explained process.',
    source_id: 'audio-2',
    source_type: 'audio_conversation',
    from_identifier: '+1 (555) 123-4567',
    to_identifiers: ['+1 (555) 987-6543'],
    attachments: [],
    has_attachments: false,
    duration_seconds: 423, // ~7 minutes
    transcript_available: true,
    transcript_id: 'transcript-2',
    audio_conversation_id: 'audio-2',
  },
  {
    id: 'call-3',
    type: 'phone_call',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(), // 1 week ago
    content:
      'Initial consultation call. Discussed accident details and gathered information.',
    source_id: 'audio-3',
    source_type: 'audio_conversation',
    from_identifier: '+1 (555) 987-6543',
    to_identifiers: ['+1 (555) 123-4567'],
    attachments: [],
    has_attachments: false,
    duration_seconds: 1832, // ~30 minutes
    transcript_available: true,
    transcript_id: 'transcript-3',
    audio_conversation_id: 'audio-3',
  },

  // Emails
  {
    id: 'email-1',
    type: 'email',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(), // 5 hours ago
    subject: 'Settlement Offer - Action Required',
    content: `Dear Mr. Smith,

I hope this email finds you well. I am writing to inform you that we have received a settlement offer from the insurance company regarding your claim.

The offer details are as follows:
- Total settlement amount: $45,000
- Medical expenses coverage: $28,500
- Pain and suffering: $16,500

Please review this offer carefully. I recommend we schedule a call to discuss the pros and cons of accepting vs. negotiating for a higher amount.

Please let me know your availability this week.

Best regards,
Sarah Johnson
Smith & Associates`,
    preview:
      'I am writing to inform you that we have received a settlement offer...',
    source_id: 'front-1',
    source_type: 'front',
    from_identifier: 'sarah@smithlaw.com',
    to_identifiers: ['john.smith@email.com'],
    attachments: [
      {
        id: 'att-1',
        filename: 'Settlement_Offer_2024.pdf',
        content_type: 'application/pdf',
        size_bytes: 245678,
        download_url: '#',
        source_type: 'front',
      },
    ],
    has_attachments: true,
    front_conversation_id: 'front-conv-1',
    transmission_status: 'delivered',
  },
  {
    id: 'email-2',
    type: 'email',
    direction: 'inbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(), // 3 hours ago
    subject: 'Re: Settlement Offer - Action Required',
    content: `Hi Sarah,

Thank you for sending this over. The amount seems lower than what we discussed initially.

I'm free tomorrow afternoon or Thursday morning to discuss. Can we aim for a higher settlement?

Also, I've attached the additional medical bills from my recent follow-up appointments.

Thanks,
John`,
    preview: 'Thank you for sending this over. The amount seems lower than...',
    source_id: 'front-2',
    source_type: 'front',
    from_identifier: 'john.smith@email.com',
    to_identifiers: ['sarah@smithlaw.com'],
    attachments: [
      {
        id: 'att-2',
        filename: 'Medical_Bills_Dec2024.pdf',
        content_type: 'application/pdf',
        size_bytes: 892341,
        download_url: '#',
        source_type: 'front',
      },
      {
        id: 'att-3',
        filename: 'Physical_Therapy_Invoice.pdf',
        content_type: 'application/pdf',
        size_bytes: 156234,
        download_url: '#',
        source_type: 'front',
      },
    ],
    has_attachments: true,
    front_conversation_id: 'front-conv-1',
    transmission_status: 'delivered',
  },
  {
    id: 'email-3',
    type: 'email',
    direction: 'outbound',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(), // 5 days ago
    subject: 'Medical Records Request - Status Update',
    content: `Dear Mr. Smith,

I wanted to update you on the status of your medical records request.

We have successfully obtained records from:
✓ City General Hospital
✓ Dr. Martinez (Primary Care)
✓ Valley Physical Therapy

We are still waiting on:
○ MRI Imaging Center (expected within 5 business days)

I will notify you once all records have been received.

Best regards,
Sarah Johnson`,
    preview:
      'I wanted to update you on the status of your medical records request...',
    source_id: 'front-3',
    source_type: 'front',
    from_identifier: 'sarah@smithlaw.com',
    to_identifiers: ['john.smith@email.com'],
    attachments: [],
    has_attachments: false,
    front_conversation_id: 'front-conv-2',
    transmission_status: 'delivered',
  },
];

const MOCK_ATTACHMENTS: CommunicationAttachment[] = [
  {
    id: 'att-1',
    filename: 'Settlement_Offer_2024.pdf',
    content_type: 'application/pdf',
    size_bytes: 245678,
    download_url: '#',
    source_type: 'front',
  },
  {
    id: 'att-2',
    filename: 'Medical_Bills_Dec2024.pdf',
    content_type: 'application/pdf',
    size_bytes: 892341,
    download_url: '#',
    source_type: 'front',
  },
  {
    id: 'att-3',
    filename: 'Physical_Therapy_Invoice.pdf',
    content_type: 'application/pdf',
    size_bytes: 156234,
    download_url: '#',
    source_type: 'front',
  },
  {
    id: 'att-4',
    filename: 'accident_photo_1.jpg',
    content_type: 'image/jpeg',
    size_bytes: 2456123,
    thumbnail_url: 'https://placehold.co/100x100/e2e8f0/475569?text=Photo',
    download_url: '#',
    source_type: 'front',
  },
  {
    id: 'att-5',
    filename: 'accident_photo_2.jpg',
    content_type: 'image/jpeg',
    size_bytes: 1987654,
    thumbnail_url: 'https://placehold.co/100x100/e2e8f0/475569?text=Photo',
    download_url: '#',
    source_type: 'front',
  },
  {
    id: 'att-6',
    filename: 'Police_Report.pdf',
    content_type: 'application/pdf',
    size_bytes: 567890,
    download_url: '#',
    source_type: 'blob',
  },
];

const MOCK_STATS: ClientCommunicationsResponse['stats'] = {
  total_sms: 8,
  total_calls: 3,
  total_emails: 3,
  total_attachments: 6,
};

const MOCK_FOLLOW_UP_STATUS: ClientCommunicationsResponse['follow_up_status'] =
  {
    requires_follow_up: false,
    days_since_last_contact: 0,
    last_contacted_at: new Date().toISOString(),
  };
// ============================================

interface ClientCommunicationsContextValue {
  matterId: string;

  // Data
  communications: UnifiedCommunication[];
  attachments: CommunicationAttachment[];
  isLoading: boolean;
  isLoadingAttachments: boolean;
  error: Error | null;
  stats: ClientCommunicationsResponse['stats'] | null;
  followUpStatus: ClientCommunicationsResponse['follow_up_status'] | null;

  // Filters
  activeTab: ClientCommunicationsTabId;
  setActiveTab: (tab: ClientCommunicationsTabId) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Local state (localStorage)
  bookmarkedIds: Set<string>;
  toggleBookmark: (id: string) => void;
  readIds: Set<string>;
  markAsRead: (id: string) => void;
  isBookmarked: (id: string) => boolean;
  isRead: (id: string) => boolean;
  getBookmarkedCommunications: () => UnifiedCommunication[];

  // Actions
  refreshCommunications: () => void;
  loadMore: () => void;
  hasMore: boolean;

  // Selected attachments for download
  selectedAttachments: Set<string>;
  toggleAttachmentSelection: (id: string) => void;
  clearAttachmentSelection: () => void;

  // Optimistic updates for sending messages
  addPendingMessage: (content: string) => string;
  removePendingMessage: (id: string) => void;
  markPendingMessageSent: (id: string) => void;
  markPendingMessageFailed: (id: string, errorMessage?: string) => void;
  retryPendingMessage: (id: string) => Promise<void>;
  getPendingMessageContent: (id: string) => string | undefined;

  // Detail pane selection
  selectedCommunication: UnifiedCommunication | null;
  selectCommunication: (communication: UnifiedCommunication | null) => void;
  isDetailPaneOpen: boolean;
}

const ClientCommunicationsContext =
  createContext<ClientCommunicationsContextValue | null>(null);

interface ClientCommunicationsProviderProps {
  matterId: string;
  children: React.ReactNode;
}

export const ClientCommunicationsProvider: React.FC<
  ClientCommunicationsProviderProps
> = ({ matterId, children }) => {
  const [activeTab, setActiveTabState] =
    useState<ClientCommunicationsTabId>('all');
  const [searchQuery, setSearchQueryState] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(
    new Set(),
  );
  const [pendingMessages, setPendingMessages] = useState<
    UnifiedCommunication[]
  >([]);
  const [selectedCommunication, setSelectedCommunication] =
    useState<UnifiedCommunication | null>(null);

  // Analytics
  const capture = usePostHogCapture();
  const hasTrackedInitialView = useRef(false);

  // Local state for bookmarks and read status
  const {
    bookmarkedIds,
    readIds,
    toggleBookmark: toggleBookmarkLocal,
    markAsRead,
    isBookmarked,
    isRead,
  } = useClientCommunicationsLocalState(matterId);

  // Polling interval for real-time updates (10 seconds - balanced for UX vs server load)
  const POLLING_INTERVAL_MS = 10_000;

  // Note: We intentionally do NOT auto-mark communications as viewed on mount.
  // Calling markViewed() sets last_comms_viewed_at = now(), which causes all
  // existing messages (with earlier front_created_at) to appear as "read",
  // even if the user hasn't actually seen them. The unread count in the sidebar
  // should reflect actual unread messages until user explicitly marks them read.
  // TODO: Add a "Mark all as read" button that calls useMarkCommunicationsViewedMutation
  const [syncEmails] = useSyncEmailsMutation();

  // Sync emails once on initial mount to ensure fresh data
  // Webhooks should handle ongoing sync, but this catches up any gaps
  useEffect(() => {
    syncEmails({ matterId });
  }, [matterId, syncEmails]);

  // Track initial tab view
  useEffect(() => {
    if (!hasTrackedInitialView.current) {
      hasTrackedInitialView.current = true;
      capture(CLIENT_COMMS_EVENTS.TAB_VIEWED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
        [CLIENT_COMMS_PROPERTIES.TAB_NAME]: 'all',
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    }
  }, [matterId, capture]);

  // Wrapped setActiveTab with analytics
  const setActiveTab = useCallback(
    (tab: ClientCommunicationsTabId) => {
      const previousTab = activeTab;
      setActiveTabState(tab);
      capture(CLIENT_COMMS_EVENTS.TAB_CHANGED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
        [CLIENT_COMMS_PROPERTIES.TAB_NAME]: tab,
        [CLIENT_COMMS_PROPERTIES.PREVIOUS_TAB]: previousTab,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    },
    [activeTab, matterId, capture],
  );

  // Wrapped setSearchQuery with analytics (debounced tracking)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryState(query);

      // Debounce search tracking to avoid excessive events
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (query.trim()) {
        searchTimeoutRef.current = setTimeout(() => {
          capture(CLIENT_COMMS_EVENTS.SEARCH_PERFORMED, {
            [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
            [CLIENT_COMMS_PROPERTIES.HAS_SEARCH_QUERY]: true,
            [CLIENT_COMMS_PROPERTIES.TAB_NAME]: activeTab,
            [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
            [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
          });
        }, 1000);
      }
    },
    [matterId, activeTab, capture],
  );

  // Wrapped toggleBookmark with analytics
  const toggleBookmark = useCallback(
    (id: string) => {
      const wasBookmarked = bookmarkedIds.has(id);
      toggleBookmarkLocal(id);
      capture(
        wasBookmarked
          ? CLIENT_COMMS_EVENTS.COMMUNICATION_UNBOOKMARKED
          : CLIENT_COMMS_EVENTS.COMMUNICATION_BOOKMARKED,
        {
          [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
          [CLIENT_COMMS_PROPERTIES.COMMUNICATION_ID]: id,
          [CLIENT_COMMS_PROPERTIES.BOOKMARKED_COUNT]: wasBookmarked
            ? bookmarkedIds.size - 1
            : bookmarkedIds.size + 1,
          [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
          [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
        },
      );
    },
    [bookmarkedIds, toggleBookmarkLocal, matterId, capture],
  );

  // Determine API filter type based on active tab
  const apiTypeFilter = useMemo(() => {
    switch (activeTab) {
      case 'texts':
        return 'sms';
      case 'calls':
        return 'phone_call';
      case 'emails':
        return 'email';
      default:
        return 'all';
    }
  }, [activeTab]);

  // Fetch communications with polling for real-time updates
  const {
    data: communicationsDataApi,
    isLoading: isLoadingApi,
    error: errorApi,
    refetch: refreshCommunicationsApi,
  } = useGetClientCommunicationsQuery(
    {
      matterId,
      params: {
        type: apiTypeFilter as 'sms' | 'email' | 'phone_call' | 'all',
        search: searchQuery || undefined,
      },
    },
    {
      skip: activeTab === 'attachments' || USE_MOCK_DATA,
      pollingInterval: POLLING_INTERVAL_MS,
      skipPollingIfUnfocused: true,
    },
  );

  // Fetch attachments when on attachments tab
  const { data: attachmentsDataApi, isLoading: isLoadingAttachmentsApi } =
    useGetClientAttachmentsQuery(
      { matterId },
      {
        skip: activeTab !== 'attachments' || USE_MOCK_DATA,
      },
    );

  // Get mock phone calls for merging
  const mockPhoneCalls = MOCK_COMMUNICATIONS.filter(
    (comm) => comm.type === 'phone_call',
  );

  // Use mock data or API data
  const communicationsData = USE_MOCK_DATA
    ? {
        communications: MOCK_COMMUNICATIONS.filter((comm) => {
          if (apiTypeFilter === 'all') return true;
          if (apiTypeFilter === 'sms') return comm.type === 'sms';
          if (apiTypeFilter === 'email') return comm.type === 'email';
          if (apiTypeFilter === 'phone_call') return comm.type === 'phone_call';
          return true;
        }),
        stats: MOCK_STATS,
        follow_up_status: MOCK_FOLLOW_UP_STATUS,
        has_more: false,
        total_count: MOCK_COMMUNICATIONS.length,
      }
    : USE_MOCK_CALLS && communicationsDataApi
      ? {
          ...communicationsDataApi,
          communications: [
            ...(communicationsDataApi.communications || []),
            ...(apiTypeFilter === 'all' || apiTypeFilter === 'phone_call'
              ? mockPhoneCalls
              : []),
          ],
        }
      : communicationsDataApi;

  const attachmentsData = USE_MOCK_DATA
    ? {
        attachments: MOCK_ATTACHMENTS,
        has_more: false,
        total_count: MOCK_ATTACHMENTS.length,
      }
    : attachmentsDataApi;

  // Show loading state while API is loading
  const isLoading = USE_MOCK_DATA ? false : isLoadingApi;
  const isLoadingAttachments = USE_MOCK_DATA ? false : isLoadingAttachmentsApi;
  const error = USE_MOCK_DATA ? null : errorApi;
  const refreshCommunications = useCallback(() => {
    if (!USE_MOCK_DATA) {
      refreshCommunicationsApi();
    }
  }, [refreshCommunicationsApi]);

  // Filter communications based on search query (client-side filtering for instant feedback)
  // Also deduplicate by ID and by content+timestamp to handle any duplicate entries from the backend
  const filteredCommunications = useMemo(() => {
    const comms = communicationsData?.communications ?? [];

    // Deduplicate by ID (keep first occurrence)
    const seenIds = new Set<string>();
    const deduplicatedById = comms.filter((comm) => {
      if (seenIds.has(comm.id)) {
        return false;
      }
      seenIds.add(comm.id);
      return true;
    });

    // Also deduplicate by content + direction + timestamp (within 60s window)
    // This catches cases where the same message was stored twice with different IDs
    const seenContentKeys = new Set<string>();
    const deduplicatedComms = deduplicatedById.filter((comm) => {
      // Only deduplicate outbound SMS messages (where duplicates are most likely to occur)
      if (comm.type === 'sms' && comm.direction === 'outbound') {
        const timestamp = new Date(comm.timestamp).getTime();
        // Create a key from content + direction + timestamp bucket (rounded to 60s)
        const timeBucket = Math.floor(timestamp / 60_000);
        const contentKey = `${comm.content}|${comm.direction}|${timeBucket}`;
        if (seenContentKeys.has(contentKey)) {
          return false;
        }
        seenContentKeys.add(contentKey);
      }
      return true;
    });

    if (!searchQuery) return deduplicatedComms;

    const lowerQuery = searchQuery.toLowerCase();
    return deduplicatedComms.filter(
      (comm) =>
        comm.content?.toLowerCase().includes(lowerQuery) ||
        comm.subject?.toLowerCase().includes(lowerQuery) ||
        comm.preview?.toLowerCase().includes(lowerQuery),
    );
  }, [communicationsData?.communications, searchQuery]);

  // Get bookmarked communications
  const getBookmarkedCommunications = useCallback(() => {
    return filteredCommunications.filter((comm) => bookmarkedIds.has(comm.id));
  }, [filteredCommunications, bookmarkedIds]);

  // Attachment selection handlers
  const toggleAttachmentSelection = useCallback((id: string) => {
    setSelectedAttachments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearAttachmentSelection = useCallback(() => {
    setSelectedAttachments(new Set());
  }, []);

  // Load more (pagination placeholder - can be enhanced later)
  const loadMore = useCallback(() => {
    // TODO: Implement cursor-based pagination
  }, []);

  // Optimistic message handling
  const addPendingMessage = useCallback((content: string): string => {
    const tempId = `pending-${Date.now()}`;
    const pendingMessage: UnifiedCommunication = {
      id: tempId,
      type: 'sms',
      direction: 'outbound',
      timestamp: new Date().toISOString(),
      content,
      source_id: tempId,
      source_type: 'conversation',
      from_identifier: 'user',
      to_identifiers: [],
      attachments: [],
      has_attachments: false,
      transmission_status: 'pending',
    };
    setPendingMessages((prev) => [...prev, pendingMessage]);
    return tempId;
  }, []);

  const removePendingMessage = useCallback((id: string) => {
    setPendingMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  const markPendingMessageSent = useCallback((id: string) => {
    setPendingMessages((prev) =>
      prev.map((msg) =>
        msg.id === id
          ? {
              ...msg,
              transmission_status: 'sent',
            }
          : msg,
      ),
    );
  }, []);

  const markPendingMessageFailed = useCallback(
    (id: string, errorMessage?: string) => {
      setPendingMessages((prev) =>
        prev.map((msg) =>
          msg.id === id
            ? {
                ...msg,
                transmission_status: 'failed',
                transmission_error: errorMessage,
              }
            : msg,
        ),
      );
    },
    [],
  );

  const getPendingMessageContent = useCallback(
    (id: string): string | undefined => {
      return pendingMessages.find((msg) => msg.id === id)?.content;
    },
    [pendingMessages],
  );

  const [sendSms] = useTreatmentSendSmsMutation();

  const retryPendingMessage = useCallback(
    async (id: string) => {
      const message = pendingMessages.find((msg) => msg.id === id);
      if (!message) return;

      // Reset to pending status
      setPendingMessages((prev) =>
        prev.map((msg) =>
          msg.id === id
            ? {
                ...msg,
                transmission_status: 'pending',
                transmission_error: undefined,
              }
            : msg,
        ),
      );

      try {
        await sendSms({
          sendTreatmentSmsRequest: {
            matter_id: matterId,
            message_text: message.content,
          },
        }).unwrap();

        // Success - mark as sent, will be removed when real message arrives from webhook
        markPendingMessageSent(id);
        refreshCommunications();
      } catch (err) {
        // Extract error message from response
        const smsError = err as { data?: { message?: string; error?: string } };
        const errorMessage =
          smsError?.data?.message ||
          smsError?.data?.error ||
          'Failed to send SMS';
        markPendingMessageFailed(id, errorMessage);
      }
    },
    [
      pendingMessages,
      matterId,
      sendSms,
      markPendingMessageSent,
      refreshCommunications,
      markPendingMessageFailed,
    ],
  );

  // Select a communication to show in the detail pane
  const selectCommunication = useCallback(
    (communication: UnifiedCommunication | null) => {
      setSelectedCommunication(communication);
      if (communication) {
        markAsRead(communication.id);
        capture(CLIENT_COMMS_EVENTS.COMMUNICATION_SELECTED, {
          [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
          [CLIENT_COMMS_PROPERTIES.COMMUNICATION_ID]: communication.id,
          [CLIENT_COMMS_PROPERTIES.COMMUNICATION_TYPE]: communication.type,
          [CLIENT_COMMS_PROPERTIES.DIRECTION]: communication.direction,
          [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
          [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
        });
      }
    },
    [markAsRead, matterId, capture],
  );

  // Computed value for whether detail pane should be open
  const isDetailPaneOpen = selectedCommunication !== null;

  // Merge pending messages with fetched communications
  // Backend returns newest-first, so pending messages go at the START (they're the newest)
  // Views then reverse to show oldest-first, putting pending at the bottom
  // Filter out pending messages that already have a matching real message (to avoid duplicates during the brief
  // window between when polling returns the new message and when removePendingMessage is called)
  const allCommunications = useMemo(() => {
    const deduplicatedPending = pendingMessages.filter((pending) => {
      // Check if there's a real message with matching content (within 60 seconds)
      const pendingTime = new Date(pending.timestamp).getTime();
      const hasMatchingReal = filteredCommunications.some((real) => {
        if (real.content !== pending.content) return false;
        if (real.direction !== pending.direction) return false;
        const realTime = new Date(real.timestamp).getTime();
        // Consider it a match if within 60 seconds
        return Math.abs(realTime - pendingTime) < 60_000;
      });
      return !hasMatchingReal;
    });
    return [...deduplicatedPending, ...filteredCommunications];
  }, [filteredCommunications, pendingMessages]);

  const value: ClientCommunicationsContextValue = useMemo(
    () => ({
      matterId,
      communications: allCommunications,
      attachments: attachmentsData?.attachments ?? [],
      isLoading,
      isLoadingAttachments,
      error: error as Error | null,
      stats: communicationsData?.stats ?? null,
      followUpStatus: communicationsData?.follow_up_status ?? null,
      activeTab,
      setActiveTab,
      searchQuery,
      setSearchQuery,
      bookmarkedIds,
      toggleBookmark,
      readIds,
      markAsRead,
      isBookmarked,
      isRead,
      getBookmarkedCommunications,
      refreshCommunications,
      loadMore,
      hasMore: communicationsData?.has_more ?? false,
      selectedAttachments,
      toggleAttachmentSelection,
      clearAttachmentSelection,
      addPendingMessage,
      removePendingMessage,
      markPendingMessageSent,
      markPendingMessageFailed,
      retryPendingMessage,
      getPendingMessageContent,
      selectedCommunication,
      selectCommunication,
      isDetailPaneOpen,
    }),
    [
      matterId,
      allCommunications,
      attachmentsData?.attachments,
      isLoading,
      isLoadingAttachments,
      error,
      communicationsData?.stats,
      communicationsData?.follow_up_status,
      communicationsData?.has_more,
      activeTab,
      setActiveTab,
      searchQuery,
      setSearchQuery,
      bookmarkedIds,
      toggleBookmark,
      readIds,
      markAsRead,
      isBookmarked,
      isRead,
      getBookmarkedCommunications,
      refreshCommunications,
      loadMore,
      selectedAttachments,
      toggleAttachmentSelection,
      clearAttachmentSelection,
      addPendingMessage,
      removePendingMessage,
      markPendingMessageSent,
      markPendingMessageFailed,
      retryPendingMessage,
      getPendingMessageContent,
      selectedCommunication,
      selectCommunication,
      isDetailPaneOpen,
    ],
  );

  return (
    <ClientCommunicationsContext.Provider value={value}>
      {children}
    </ClientCommunicationsContext.Provider>
  );
};

export const useClientCommunications = (): ClientCommunicationsContextValue => {
  const context = useContext(ClientCommunicationsContext);
  if (!context) {
    throw new Error(
      'useClientCommunications must be used within a ClientCommunicationsProvider',
    );
  }
  return context;
};
